import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { PlusIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CircleIcon, DragIcon } from '@/components/icons';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ModalWarning from '@/components/ui/custom/modals/ModalWarning';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { generateChapterContent } from '@/data/services/astro.ai/generateServices';
import { GetBookParams } from '@/data/services/bookServices';
import {
  ChapterParams,
  createChapter,
  UpdateChaptersOrder,
} from '@/data/services/chapterServices';
import Book, { bookType } from '@/models/Book';
import Chapter from '@/models/Chapter';
import { buildMaterialUrl } from '@/utils/book/bookUrl';
import { dropHandler } from '@/utils/dragdrop';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ChapterWithSaveStatus } from './EditMaterialPage';
import alert from '@/utils/shared/alert';

export type OnGenerateChapter = (
  chapter: Omit<Chapter, 'id' | 'book'> & { id?: number },
) => void;

type ChaptersProps = {
  courseSlug?: string;
  lessonId: number;
  blockId?: number;
  book: Book;
  chapters: Array<Chapter | ChapterWithSaveStatus>;
  selectedChapter: Chapter | undefined;
  editMode?: boolean | true;
  onCreate?: UseMutateFunction<Chapter, any, GetBookParams, unknown>;
  onUpdateOrder?: UseMutateFunction<void, any, UpdateChaptersOrder, unknown>;
  onDelete?: UseMutateFunction<void, any, ChapterParams, unknown>;
  updatingBook?: boolean;
  onSelectChapter?: (chapter: Chapter) => void;
  onGenerate?: OnGenerateChapter;
};

export const Chapters = ({
  courseSlug,
  lessonId,
  blockId,
  book,
  chapters,
  editMode,
  onCreate,
  onUpdateOrder,
  onDelete,
  updatingBook,
  selectedChapter,
  onSelectChapter,
  onGenerate,
}: ChaptersProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });

  const { allowBigChanges } = useCourseEditing();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const navigate = useNavigate();
  async function handleUnsavedChapters() {
    const unsavedChapters = chapters.reduce(reduceUnsavedChapters, []);
    if (unsavedChapters.length === 0) return chapters;

    return await Promise.all(
      chapters.map((chapter, chapterIndex) =>
        replaceNewChapters(unsavedChapters, chapter, chapterIndex),
      ),
    );

    type ChapterWithIndex = {
      index: number;
      chapter: Chapter | ChapterWithSaveStatus;
    };
    function reduceUnsavedChapters(
      unsavedChapters: ChapterWithIndex[],
      chapter: ChapterWithSaveStatus | Chapter,
      index: number,
    ) {
      if (isChapterSaved(chapter)) return unsavedChapters;
      const chapterWithIndex = {
        index,
        chapter,
      };
      return [...unsavedChapters, chapterWithIndex];
    }
    function replaceNewChapters(
      unsavedChapters: ChapterWithIndex[],
      chapter: ChapterWithSaveStatus | Chapter,
      chapterIndex: number,
    ) {
      const unsavedChapter = unsavedChapters.find(
        ({ index }) => index === chapterIndex,
      )?.chapter;
      if (!unsavedChapter) return chapter;
      return createChapter(
        { bookId: book.id, lessonId, bookType: book.bookType },
        { title: chapter.title, content: chapter.content },
      );
    }
  }

  function handleChapterOrderNavigation(
    selectedChapter: Chapter | ChapterWithSaveStatus,
    allChapters: Chapter[],
  ) {
    if (isChapterSaved(selectedChapter)) return;

    const savedChapter = allChapters.find(
      chapter => chapter.order === selectedChapter.order,
    );
    if (!savedChapter) return;
    navigate(
      getChapterUrl(savedChapter, lessonId, book.bookType, courseSlug, blockId),
      { replace: true },
    );
  }

  const handleDragEnd = async (res: DropResult) => {
    if (!selectedChapter) return;
    const allChapters = await handleUnsavedChapters();
    const reordenedResult = dropHandler(res, allChapters);

    onUpdateOrder?.({
      params: {
        bookId: book.id,
        lessonId,
        bookType: book.bookType,
      },
      chapters: reordenedResult,
    });

    handleChapterOrderNavigation(selectedChapter, allChapters);
  };

  async function handleDeleteChapter() {
    if (!selectedChapter) return;
    onDelete?.({
      chapterId: selectedChapter.id,
      bookId: book.id,
      bookType: book.bookType,
      lessonId,
    });
    setIsOpenModal(false);
  }

  async function handleCreateChapter() {
    onCreate?.({
      bookId: book.id,
      bookType: book.bookType,
      lessonId,
    });
  }

  function handleGenerateChapter() {
    const order = chapters.length + 1;
    return generateChapterContent({
      lessonId,
      order,
    });
  }

  const { mutate: generateChapter, isPending: isGeneratingChapter } =
    useMutation({
      mutationFn: handleGenerateChapter,
      onSuccess: data => {
        onGenerate?.({ ...data, order: chapters.length + 1 });
      },
      onError: error => {
        alert.error(getErrorMessage(error));
      },
    });

  return (
    <div className="flex flex-col gap-5 sticky top-3.5 self-start mt-3.5 w-[30%]">
      <ModalWarning
        isLoading={updatingBook}
        visible={isOpenModal}
        translationString="modalDeleteChapter"
        objectTarget={selectedChapter?.title}
        onClickConfirm={handleDeleteChapter}
        onClickCancel={() => setIsOpenModal(false)}
      />

      <Text
        text={t('chapters')}
        size="text-18"
        className="text-base-content"
        format="rubik-500"
      />

      <DragDropContext onDragEnd={res => handleDragEnd(res)}>
        <Droppable droppableId="chapters">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <div className="flex flex-col gap-2.5">
                  {chapters.map((chapter, index) => (
                    <DraggableChapter
                      key={chapter.id}
                      chapter={chapter}
                      index={index}
                      isActive={selectedChapter?.id === chapter.id}
                      setIsOpenModal={setIsOpenModal}
                      updatingBook={updatingBook}
                      chapters={chapters}
                      editMode={editMode}
                      bookType={book.bookType}
                      lessonId={lessonId}
                      courseSlug={courseSlug}
                      blockId={blockId}
                      onSelectChapter={onSelectChapter}
                    />
                  ))}
                </div>
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <ConditionalRenderer condition={editMode}>
        <div className="flex gap-1 items-center flex-wrap">
          <Tooltip
            text={!allowBigChanges && editMode ? t('cantModify') : null}
            className="w-fit"
          >
            <MainButton
              text={t('newChapterButton')}
              icon={<PlusIcon />}
              loading={updatingBook}
              disabled={!allowBigChanges || updatingBook}
              onClick={handleCreateChapter}
            />
          </Tooltip>
          <Tooltip
            text={!allowBigChanges && editMode ? t('cantModify') : null}
            className="w-fit"
          >
            <MainButton
              text={t('generateChapterButton')}
              icon={<SparklesIcon />}
              loading={updatingBook || isGeneratingChapter}
              disabled={!allowBigChanges || updatingBook}
              onClick={() => generateChapter()}
            />
          </Tooltip>
        </div>
      </ConditionalRenderer>
    </div>
  );
};

type DraggableChapterProps = Pick<
  ChaptersProps,
  | 'editMode'
  | 'chapters'
  | 'lessonId'
  | 'courseSlug'
  | 'blockId'
  | 'onSelectChapter'
> & {
  index: number;
  chapter: Chapter | ChapterWithSaveStatus;
  isActive: boolean;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  updatingBook?: boolean;
  bookType: bookType;
};

function DraggableChapter({
  index,
  chapter,
  isActive,
  bookType,
  setIsOpenModal,
  updatingBook,
  editMode,
  chapters,
  lessonId,
  courseSlug,
  blockId,
  onSelectChapter,
}: DraggableChapterProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });

  const navigate = useNavigate();

  const { allowBigChanges } = useCourseEditing();

  const checkIfCanDeleteChapter = (
    chapter: Chapter,
    chapters: null | Chapter[],
  ) => {
    if (chapters && chapters?.length <= 1) {
      alert.warning(t('oneChapterMessage'));
      return;
    }

    navigate(getChapterUrl(chapter, lessonId, bookType, courseSlug, blockId), {
      replace: true,
    });
    setIsOpenModal(true);
  };

  const icon = editMode ? (
    <DragIcon className="cursor-pointer" />
  ) : (
    <CircleIcon className="w-4 h-4 shrink-0 text-primary" />
  );

  const chapterStatus = (isActive: boolean, isEditMode?: boolean) => {
    if (isActive) {
      return 'font-700 text-primary bg-primary-content rounded-md';
    }
    if (isEditMode) {
      return 'font-400 text-base-content hover:text-primary/75 cursor-pointer';
    }

    return 'font-700 text-primary cursor-pointer';
  };

  const onNavigate = () => {
    if (editMode) {
      navigate(
        getChapterUrl(chapter, lessonId, bookType, courseSlug, blockId),
        { replace: true },
      );
    }
    onSelectChapter?.(chapter);
  };

  return (
    <Draggable
      draggableId={String(chapter.id)}
      index={index}
      isDragDisabled={!editMode || !allowBigChanges || updatingBook}
      key={chapter.id}
    >
      {provided => (
        <div
          onClick={onNavigate}
          className="flex items-center gap-1.5 justify-between"
          data-testid="chapterItem"
          {...provided.draggableProps}
          ref={provided.innerRef}
        >
          <div
            className="gap-2.5 flex items-center"
            {...provided.dragHandleProps}
          >
            <Tooltip
              text={!allowBigChanges && editMode ? t('cantModify') : null}
            >
              {icon}
            </Tooltip>
          </div>

          <div
            className={`flex items-center gap-1.5 justify-between w-full px-2 py-1.5 overflow-x-hidden ${chapterStatus(
              isActive,
              editMode,
            )}`}
          >
            <Text
              text={chapter.title}
              size="text-14"
              className="text-left truncate"
            />

            <ConditionalRenderer condition={editMode}>
              <Tooltip
                placement="left"
                text={!allowBigChanges && editMode ? t('cantModify') : null}
                className="w-fit"
              >
                <IconButton
                  icon={<XMarkIcon className="text-neutral/50" />}
                  className="w-4"
                  disabled={!allowBigChanges || updatingBook}
                  onClick={() => checkIfCanDeleteChapter(chapter, chapters)}
                />
              </Tooltip>
            </ConditionalRenderer>
          </div>
        </div>
      )}
    </Draggable>
  );
}

function isChapterSaved(chapter: Chapter | ChapterWithSaveStatus) {
  return !('isSaved' in chapter) || chapter.isSaved;
}

function getChapterUrl(
  chapter: Chapter,
  lessonId: number,
  bookType: bookType,
  courseSlug?: string,
  blockId?: number,
) {
  return buildMaterialUrl({
    lessonId,
    bookType,
    bookId: chapter.book,
    slugCourseName: courseSlug,
    blockId,
    chapterId: chapter.id,
  });
}
